import { SearchIcon, SquarePenIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import { Kbd } from "../ui/kbd";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { useCloseSidebarOnNavigate } from "./useCloseSidebarOnNavigate";
import { useCommandPaletteStore } from "~/commandPaletteStore";
import type { SidebarNewThreadRoute } from "./sidebarNewThreadRoute";

// ---------------------------------------------------------------------------
// SearchAndCommandCenter — MVP shell actions
//
// Search and "new thread" only. Automations is intentionally not surfaced in
// the rail, and Plugins moved down to the account footer beside Usage and
// Settings; both routes stay reachable by URL.
// ---------------------------------------------------------------------------

interface SearchAndCommandCenterProps {
  readonly commandPaletteShortcutLabel: string | null;
  /**
   * Where "New thread" navigates. Carries the active project so the composer
   * opens on the project the user is in rather than defaulting to whichever one
   * sits first in the sidebar drag order (CMMD-2266). Defaults to a bare `/new`.
   */
  readonly newThreadRoute?: SidebarNewThreadRoute;
}

const BARE_NEW_THREAD_ROUTE: SidebarNewThreadRoute = { to: "/new" };

const NAV_ROW_CLASS = "text-sidebar-muted-foreground hover:text-sidebar-foreground";

export const SearchAndCommandCenter = memo(function SearchAndCommandCenter({
  commandPaletteShortcutLabel,
  newThreadRoute = BARE_NEW_THREAD_ROUTE,
}: SearchAndCommandCenterProps) {
  const closeSidebarOnNavigate = useCloseSidebarOnNavigate();
  const setCommandPaletteOpen = useCommandPaletteStore((store) => store.setOpen);
  const handleOpenCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
    closeSidebarOnNavigate();
  }, [closeSidebarOnNavigate, setCommandPaletteOpen]);

  return (
    <SidebarGroup className="gap-1 p-0">
      {/* Search and "new thread" share one line, the way t3code's rail does.
          Search is the wide field because it is the one you scan for; creating
          a thread is a single known target, so it collapses to its glyph and
          stops competing with the label beside it. */}
      <SidebarMenu className="flex-row items-center gap-1">
        <SidebarMenuItem className="min-w-0 flex-1">
          <SidebarMenuButton
            aria-label="Search"
            className={NAV_ROW_CLASS}
            data-testid="command-palette-trigger"
            onClick={handleOpenCommandPalette}
          >
            <SearchIcon />
            <span className="min-w-0 flex-1 truncate text-left font-medium">Search</span>
            {commandPaletteShortcutLabel ? (
              <Kbd className="h-4 min-w-0 shrink-0 rounded-sm px-1.5 text-[10px]">
                {commandPaletteShortcutLabel}
              </Kbd>
            ) : null}
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem className="shrink-0">
          <Tooltip>
            <TooltipTrigger
              render={
                <SidebarMenuButton
                  aria-label="Start a new thread"
                  className={NAV_ROW_CLASS}
                  data-testid="sidebar-new-thread-button"
                  onClick={closeSidebarOnNavigate}
                  size="icon"
                  render={
                    newThreadRoute.search ? (
                      <Link to={newThreadRoute.to} search={newThreadRoute.search} />
                    ) : (
                      <Link to={newThreadRoute.to} />
                    )
                  }
                >
                  <SquarePenIcon />
                </SidebarMenuButton>
              }
            />
            <TooltipPopup side="right">New thread</TooltipPopup>
          </Tooltip>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
});
